import { useEffect, useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import AppealStatusBanner from 'components/AppealStatusBanner';
import AdditionalDocumentsSection from 'views/shared/components/AdditionalDocumentsSection';

import { apiApplicationFormData, apiValidations } from 'api';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import { AuthContext } from 'contexts/AuthContext';

import isSuperAdmin from 'helpers/isSuperAdmin/isSuperAdmin';

import SectionMenu from './sections/SectionMenu';
import PolicyInformation from './sections/PolicyInformation';
import LOAInformation from './sections/LOAInformation';
import InsuranceCompanyInformation from './sections/InsuranceCompanyInformation';
import Applicant from './sections/Applicant';
import PolicyPeriod from './sections/PolicyPeriod';
import Vehicles from './sections/Vehicles';
import Drivers from './sections/Drivers';
import Claims from './sections/Claims';
import Convictions from './sections/Convictions';
import RatingInformation from './sections/RatingInformation';
import Remarks from './sections/Remarks';
import Submit from './sections/Submit';
import InsuranceCoverages from './sections/InsuranceCoverages';
import MethodOfPayment from './sections/MethodOfPayment';
import ReportOfBrokerAgent from './sections/ReportOfBrokerAgent';
import MissingFields from './sections/MissingFields';
import Acknowledgement from './sections/Acknowledgement';
import BaraReferralRouter from 'components/bara/BaraReferralRouter';
import BaraBrokerStatusBanner from 'components/bara/BaraBrokerStatusBanner';
import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import ReferToUnderwriting from 'views/shared/ReferToUnderwriting';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { BaraBillingEditableContext } from 'hooks/useFormBinderDisabled';
import DisclosureAndAttestation from './sections/DisclosureAndAttestation';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useBinderAdditionalFiles } from 'views/shared/hooks/useBinderAdditionalFiles';

import { storeData, resetData } from 'reducers/autoPersonalFormReducer';

import type { APIPersonalAutoApplicationFormData } from 'types/api.types';

const AutoFormView = () => {
  const { user } = useContext(AuthContext);
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const isBaraEnabled = useFeatureFlag('enable-bara');

  const isDataSet = useAppSelector((state) => state.autoPersonalForm.isDataSet);

  const {
    error,
    data: initialData,
    isFetching,
  } = useQuery({
    queryKey: ['app_form', uuid],
    queryFn: () => apiApplicationFormData(uuid),
    refetchOnWindowFocus: false,
  }) as {
    error: any;
    data: APIPersonalAutoApplicationFormData;
    isFetching: boolean;
  };

  const {
    additionalFiles,
    isUploadingFile,
    deletingFileId,
    handleFilesUpload,
    handleFileRemove,
  } = useBinderAdditionalFiles(uuid || '', initialData?.additional_files);

  const { data: validations, error: validationsError } = useQuery({
    queryKey: [`validations_${uuid}`],
    queryFn: () => apiValidations(uuid),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    dispatch(resetData());
  }, []);

  useEffect(() => {
    if (isDataSet || isFetching || !initialData?.data) return;

    dispatch(
      storeData({
        uuid,
        data: initialData.data,
        remarks: initialData.remarks,
        files: initialData.files,
        payments: initialData.payments,
        binder_pdf: initialData.binder_pdf,
        signing: initialData.signing,
        appeal: initialData.appeal,
        bara_referral: initialData.bara_referral ?? null,
        wip_status: initialData.wip_status,
        province: initialData.province,
      }),
    );
  }, [initialData, uuid, isDataSet, isFetching, dispatch]);

  if (!isDataSet) {
    return <Card textAlign="center">Preparing form...</Card>;
  }

  if (validationsError) {
    const errorData = (validationsError as any)?.error;
    const errorMessage =
      errorData?.[0]?.quote || errorData?.[0] || 'Failed to load validations';
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Validation Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (!validations) {
    return <Card textAlign="center">Loading validations...</Card>;
  }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <ApplicationFormContext.Provider
      value={{ validations: validations?.validations || [] }}
    >
      <BaraFeatureGuard silent>
        <BaraBrokerStatusBanner formStateKey="autoPersonalForm" />
      </BaraFeatureGuard>
      <SectionMenu />
      <PolicyInformation uploadLOA={initialData?.is_loa} />
      <LOAInformation />
      <InsuranceCompanyInformation />
      <Applicant />
      <PolicyPeriod />
      <Vehicles />
      <Drivers />
      {isSuperAdmin(user) && (
        <>
          <Claims />
          <Convictions />
          <RatingInformation />
          <InsuranceCoverages />
        </>
      )}
      <BaraBillingEditableContext.Provider value>
        <MethodOfPayment />
      </BaraBillingEditableContext.Provider>
      <ReportOfBrokerAgent />
      <Remarks />
      <Acknowledgement />
      <DisclosureAndAttestation />

      <AdditionalDocumentsSection
        uploadedFiles={additionalFiles}
        onFilesUpload={handleFilesUpload}
        onFileRemove={handleFileRemove}
        isUploading={isUploadingFile}
        isDeletingFileId={deletingFileId}
        formStateKey="autoPersonalForm"
      />

      {isBaraEnabled ? (
        <BaraReferralRouter formStateKey="autoPersonalForm" />
      ) : (
        <ReferToUnderwriting formStateKey="autoPersonalForm" />
      )}
      <AppealStatusBanner formStateKey="autoPersonalForm" />
      <MissingFields />
      <Submit additionalFiles={additionalFiles} />
    </ApplicationFormContext.Provider>
  );
};

export default AutoFormView;
